/* eslint-disable @next/next/no-img-element */
import { useContext, useEffect, useState } from 'react';
import { Context as PlannerItemContext } from '@context/PlannerItemContext';
import H6 from '@base/H6/H6';
import P2 from '@base/P2/P2';
import RateYourExperienceDetails from '@comp/RateYourExperience/RateYourExperienceDetails';
import {
  PLUS_HOME_FEEDBACK_SUBMITTED,
  PLANNER_CARD_CLICKED,
  PLANNER_PAGE,
  SUBSCRIPTION_FEEDBACK_CARD_VIEWED,
  SUBSCRIPTION_FEEDBACK_CARD_CLICKED,
  SUBSCRIPTION_FEEDBACK_CARD_SUBMITTED
} from '@constants/segment';
import styled from '@emotion/styled';
import { Spacings } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { sendPlannerDiagnosticData } from '@cont/PlannerV2/planner-utils/diagnostics/collector';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import useFetch from '@hooks/useFetch';
import { submitSubscriptionFeedback } from '@api/fetchers/auth';
import {
  browseTopSpacing,
  dayTitleLeftSpacing,
  plannerItemTypeHeightMap,
  PLATFORM_SUBSCRIPTION
} from '../../planner-constants';
import SubscriptionRatingStar from './SubscriptionRatingStar';

const OuterWrapper = styled.div`
  padding-top: ${browseTopSpacing}px;
  height: ${({ $height }) => $height}px;
`;

const Wrapper = styled.div`
  margin-left: ${dayTitleLeftSpacing};
  background: linear-gradient(
      0deg,
      var(--color-i-orange-8),
      var(--color-i-orange-8)
    ),
    var(--color-base-1);
  display: flex;
  justify-content: space-between;
  background-repeat: no-repeat;
  background-position: bottom right;
  box-shadow: ${boxShadowGenerator()};
  border-radius: ${Spacings.SPACING_2B};
  height: 100%;
  position: relative;
`;

const ContentWrapper = styled.div`
  width: 72%;
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_0B} ${Spacings.SPACING_8B}
    ${Spacings.SPACING_8B};
  z-index: 1;
`;

const StyledRating = styled(SubscriptionRatingStar)`
  align-items: center;
  margin-top: ${Spacings.SPACING_4B};
`;

const brownColor = 'var(--color-i-brown-4)';

const Title = styled(H6)`
  line-height: 129%;
`;

const Description = styled(P2)`
  line-height: 133%;
  margin-top: ${Spacings.SPACING_1B};
`;

const ImageWrapper = styled.div`
  position: absolute;
  right: 0;
  height: inherit;
`;
const FeedbackImage = styled.img`
  height: 100%;
`;

const SubscriptionFeedbackCardV2 = ({ metaItem }) => {
  const { refreshFeatureCards } = useContext(PlannerItemContext);
  const goalUID = useSelectedGoalUID();
  const goalSegmentData = useGoalEventProps({ goalUID });
  const {
    plannerItemType,
    itemInfo: {
      header = '',
      sub_text: subText,
      feedback_type: feedbackType,
      image_url,
      entity_title: entityTitle,
      object_uid: objectUID
    }
  } = metaItem;

  const isFeedBackTypePlatform = feedbackType === PLATFORM_SUBSCRIPTION;
  const source = isFeedBackTypePlatform
    ? SUBSCRIPTION_FEEDBACK_CARD_SUBMITTED
    : PLUS_HOME_FEEDBACK_SUBMITTED;

  const { data: feedbackOptions } = useFetch(
    `v1/feedback/${feedbackType}/?object_uid=${objectUID}`,
    { useCamelCase: false }
  );

  useEffect(() => {
    if (isFeedBackTypePlatform) {
      segment.track(SUBSCRIPTION_FEEDBACK_CARD_VIEWED, {
        ...goalSegmentData,
        last_primary_source: PLANNER_PAGE,
        last_primary_source_section: 'Planner',
        card_type: 'Platform Subscription Rating',
        entity_content_type: 'Platform Batch Group',
        entity_uid: objectUID,
        entity_title: entityTitle
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [ratingValue, setRatingValue] = useState(0);
  const height = plannerItemTypeHeightMap[plannerItemType];
  const [drawerState, setDrawerState] = useState(false);
  const handleClick = (value) => () => {
    setRatingValue(value);
    setDrawerState(true);
    const segmentData = {
      ...goalSegmentData,
      last_primary_source: PLANNER_PAGE,
      last_primary_source_section: 'Planner',
      card_type: 'Plus Subscription Rating'
    };
    if (isFeedBackTypePlatform) {
      segment.track(SUBSCRIPTION_FEEDBACK_CARD_CLICKED, {
        ...segmentData,
        card_type: 'Platform  Subscription Rating',
        entity_content_type: 'Platform Batch Group',
        entity_uid: objectUID,
        entity_title: entityTitle
      });
    } else {
      segment.track(PLANNER_CARD_CLICKED, {
        ...segmentData
      });
    }
  };

  const handleCloseRatingDialog = (closeData) => {
    setRatingValue(0);
    const hasSubmittedFeedback = closeData?.hasSubmittedFeedback;
    if (hasSubmittedFeedback) {
      sendPlannerDiagnosticData({ ratingValue });
      refreshFeatureCards().then(() => {
        setDrawerState(false);
      });
    } else {
      setDrawerState(false);
    }
  };
  return (
    <OuterWrapper $height={height}>
      <RateYourExperienceDetails
        ratingValue={ratingValue}
        setRatingValue={setRatingValue}
        feedBackDetails={feedbackOptions}
        submitFeedback={submitSubscriptionFeedback}
        objectUID={objectUID}
        isSubmittingFeedback
        extraData={{ goalUID, feedbackType }}
        drawerState={drawerState}
        onClose={handleCloseRatingDialog}
        source={source}
        goalUID={goalUID}
        entityTitle={entityTitle}
      />
      <Wrapper>
        <ContentWrapper>
          <Title color={brownColor}>{header}</Title>
          <Description color={brownColor}>{subText}</Description>
          <StyledRating value={ratingValue} setRating={handleClick} />
        </ContentWrapper>
        <ImageWrapper>
          <FeedbackImage src={image_url} alt="feedback_image" />
        </ImageWrapper>
      </Wrapper>
    </OuterWrapper>
  );
};

export default SubscriptionFeedbackCardV2;
